import { Injectable } from '@angular/core'
import { Observable, of } from 'rxjs'
import { Store } from '@ngrx/store'
import { select } from '@ngrx/store'
import { GenericCoordinates } from '../location.types'
import { LocationSearchService, LocationSearchResult, LocationDistanceService } from './location.interface'
import { GeocodeService, GeocodeResult } from './geocode.interface'
import { AwsLocationSearchService } from './aws/aws-location-search.service'
import { AwsDirectionsService } from './aws/aws-directions.service'
import { AwsGeocodeService } from './aws/aws-geocode.service'
import { RouteDistanceRequest } from '../../route-distance/route-distance.types'
import { AAAStore } from '../../../store/root-reducer'
import { GeoCoordinates, PERMISSION_STATE } from '../geocode/types'
import { setLocationServicesAvailable } from '../location.actions'
import { TaggingService } from '../../tagging/tagging.service'
import { AdobeEventService } from '../../tagging/adobe/event-adobe.service'
import { AdobeEventTypes } from '../../tagging/tagging.types'
import events from '../../tagging/events'
import { isMobileApp } from '../../../shared/utils/app-detect'
import { isIOS } from '../../../shared/utils/browser-detect'
import { isAgentAppId } from '../../ui/ui.utils'

@Injectable({ providedIn: 'root' })
export class LocationFacadeService implements LocationSearchService, LocationDistanceService, GeocodeService {
  constructor(
    private store$: Store<AAAStore>,
    private awsLocationSearchService: AwsLocationSearchService,
    private awsDirectionsService: AwsDirectionsService,
    private awsGeocodeService: AwsGeocodeService,
    private taggingService: TaggingService,
    private adobeEventService: AdobeEventService
  ) {}

  search(term: string, location?: GenericCoordinates): Observable<LocationSearchResult[]> {
    if (term === '' || term.length <= 2) {
      return of([])
    }
    return this.awsLocationSearchService.search(term, location)
  }

  calculateDistance(routeDistanceRequest: RouteDistanceRequest): Observable<number> {
    return this.awsDirectionsService.calculateDistance(routeDistanceRequest)
  }

  /**
   * Get location details from an address string
   */
  getLocationFromAddress(address: string): Observable<Array<GeocodeResult>> {
    return this.awsGeocodeService.getLocationFromAddress(address)
  }

  /**
   * Get location details from coordinates (reverse geocoding)
   */
  getLocationFromCoords(coordinates: GeoCoordinates | GenericCoordinates): Observable<Array<GeocodeResult>> {
    return this.awsGeocodeService.getLocationFromCoords(coordinates)
  }

  /**
   * Get GPS coordinates from the browser's geolocation API
   * This method is provider-agnostic as it uses the browser's native API
   *
   * @param enabled By default will be enabled
   * @param defaultCoords Default coordinates to use as fallback
   */
  getGPSCoords(enabled: Boolean = true, defaultCoords?: GeoCoordinates): Promise<GeoCoordinates> {
    return new Promise(async (resolve, reject) => {
      // fallback response with default user coords
      if (!enabled && isMobileApp()) {
        this.handleGpsError('permissions disabled', reject)
        return
      }

      let promptState = !isMobileApp() && !enabled
      if (promptState) {
        try {
          const locationPermission = await navigator.permissions.query({ name: 'geolocation' })
          promptState = locationPermission?.state === 'prompt'
          if (promptState) {
            this.adobeEventService.sendEvent({
              eventName: AdobeEventTypes.SYSTEM,
              eventValue: events.location.LOCATION_SERVICE_PROMPT
            })

            this.taggingService.setAutomatedEvent(
              events.location.LOCATION_SERVICE_PROMPT,
              events.location.LOCATION_PAGE_TYPE
            )
          }
        } catch (e) {
          console.warn('Could not determine location service permission state', e)
        }
      }

      if (isAgentAppId() || (isMobileApp() && isIOS())) {
        if (defaultCoords) {
          this.handleGpsSuccess(defaultCoords, resolve, promptState)
        } else {
          this.handleGpsError('defaultCoords is empty', reject, promptState)
        }
      } else {
        navigator.geolocation.getCurrentPosition(
          (position) => this.handleGpsSuccess({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            accuracy: position.coords.accuracy,
          }, resolve, promptState),
          (error) => {
            if (defaultCoords) {
              this.handleGpsSuccess(defaultCoords, resolve, promptState)
            } else {
              this.handleGpsError(error, reject, promptState)
            }
          },
          {
            timeout: 15000,
            enableHighAccuracy: true,
          }
        )
      }
    })
  }

  /**
   * Get current geolocation permission state
   */
  getGeolocationPermission = async (): Promise<PERMISSION_STATE> => {
    try {
      const permissionStatus = await navigator.permissions.query({ name: 'geolocation' })

      switch (permissionStatus.state) {
        case 'granted':
          return PERMISSION_STATE.GRANTED
        case 'denied':
          return PERMISSION_STATE.DENIED
        case 'prompt':
          return PERMISSION_STATE.PROMPT
        default:
          return PERMISSION_STATE.UNKNOWN
      }
    } catch (e) {
      return PERMISSION_STATE.UNKNOWN
    }
  }

  private handleGpsError(error: any, callback: (reason?: any) => void, promptState?: boolean): void {
    if (promptState) {
      this.adobeEventService.sendEvent({
        eventName: AdobeEventTypes.CTA,
        eventValue: events.location.LOCATION_SERVICE_DENIED
      })
      this.taggingService.setAutomatedEvent(
        events.location.LOCATION_SERVICE_DENIED,
        events.location.LOCATION_PAGE_TYPE
      )
    }
    this.store$.dispatch(setLocationServicesAvailable({
      payload: { available: false, coordinates: null, denied: true },
    }))
    callback(error)
  }

  private handleGpsSuccess(coordinates: GeoCoordinates, callback: (value: GeoCoordinates) => void, promptState: boolean): void {
    if (promptState) {
      this.adobeEventService.sendEvent({
        eventName: AdobeEventTypes.CTA,
        eventValue: events.location.LOCATION_SERVICE_ALLOWED
      })
      this.taggingService.setAutomatedEvent(
        events.location.LOCATION_SERVICE_ALLOWED,
        events.location.LOCATION_PAGE_TYPE
      )
    }
    this.store$.dispatch(setLocationServicesAvailable({
      payload: { available: true, coordinates },
    }))
    callback(coordinates)
  }

}


